import { useState, useCallback, useRef, useEffect } from 'react';
import { streamChat, streamConfirmation, streamNudge, ConfirmationPayload, type DisplayMessage, type SSEEvent } from '../services/agentApi';
import type { AgentContext } from './useAgentContext';

/**
 * Custom event dispatched when the agent modifies data via a write tool.
 * Listeners (FME page, ModelContext) use this to apply live updates.
 */
export const AGENT_DATA_CHANGED_EVENT = 'agent:data_changed';

export interface AgentDataChange {
    type: string;
    [key: string]: unknown;
}

function dispatchDataChanged(change: AgentDataChange) {
    window.dispatchEvent(new CustomEvent(AGENT_DATA_CHANGED_EVENT, { detail: change }));
}

export interface ChartSpec {
    chart_type: 'line' | 'bar' | 'pie';
    title: string;
    data?: Record<string, unknown>[];
    series?: Array<{
        dataKey: string;
        label?: string;
        color?: string;
    }>;
    x_axis_key?: string;
    stacked?: boolean;
    pie_data?: Array<{
        name: string;
        value: number;
        color?: string;
    }>;
}

export interface ChatMessage {
    id: string;
    role: 'user' | 'assistant';
    content: string;
    toolCalls?: Array<{
        tool_call_id: string;
        tool_name: string;
        description: string;
        status?: 'error';
    }>;
    confirmation?: ConfirmationPayload;
    charts?: ChartSpec[];
    isStreaming?: boolean;
}

let messageIdCounter = 0;
function generateMessageId(): string {
    return `msg_${Date.now()}_${++messageIdCounter}`;
}

export function useAgentChat(context: AgentContext) {
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [conversationId, setConversationId] = useState<string | null>(null);
    const [autoConfirm, setAutoConfirmState] = useState(false);
    const [isThinking, setIsThinking] = useState(false);
    const [isAvailable, setIsAvailable] = useState<boolean | null>(null);

    // Refs to avoid stale closures inside async callbacks
    const isLoadingRef = useRef(false);
    const conversationIdRef = useRef<string | null>(null);
    const autoConfirmRef = useRef(false);
    const abortControllerRef = useRef<AbortController | null>(null);
    const contextRef = useRef<AgentContext>(context);
    const sessionIdRef = useRef(0);
    const thinkingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Keep context ref in sync
    useEffect(() => { contextRef.current = context; }, [context]);

    // Thinking-state helpers: show "thinking" dots during idle gaps while loading
    const startThinking = () => {
        if (thinkingTimerRef.current) clearTimeout(thinkingTimerRef.current);
        setIsThinking(true);
    };
    const stopThinking = () => {
        if (thinkingTimerRef.current) clearTimeout(thinkingTimerRef.current);
        thinkingTimerRef.current = null;
        setIsThinking(false);
    };
    /** Call on text/chart SSE events to hide dots, then re-show after a short idle gap. */
    const markActivity = () => {
        setIsThinking(false);
        if (thinkingTimerRef.current) clearTimeout(thinkingTimerRef.current);
        thinkingTimerRef.current = setTimeout(() => {
            if (isLoadingRef.current) setIsThinking(true);
        }, 600);
    };
    /** Call on tool_call events — suppress dots without re-enable timer
     *  (the tool indicator provides ongoing visual feedback). */
    const markToolActivity = () => {
        setIsThinking(false);
        if (thinkingTimerRef.current) clearTimeout(thinkingTimerRef.current);
        thinkingTimerRef.current = null;
    };

    // Pending tool_call_id to auto-confirm (set when autoConfirm skips the card)
    const [pendingAutoConfirmId, setPendingAutoConfirmId] = useState<string | null>(null);

    // Keep refs in sync with state
    const setIsLoadingSync = (val: boolean) => {
        isLoadingRef.current = val;
        setIsLoading(val);
    };
    const setConversationIdSync = (val: string) => {
        conversationIdRef.current = val;
        setConversationId(val);
    };
    const setAutoConfirm = useCallback((val: boolean) => {
        autoConfirmRef.current = val;
        setAutoConfirmState(val);
    }, []);

    // Auto-trigger confirmation when autoConfirm is on and a pending tool arrives
    useEffect(() => {
        if (pendingAutoConfirmId !== null) {
            setPendingAutoConfirmId(null);
            handleConfirmation(pendingAutoConfirmId, true);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pendingAutoConfirmId]);

    // Helper: handle a confirmation_required event in both sendMessage and handleConfirmation
    const handleConfirmationRequired = (
        toolCallId: string,
        confirmation: ConfirmationPayload,
        messageId: string,
    ) => {
        if (autoConfirmRef.current) {
            // Skip the card, queue an automatic confirm
            setMessages(prev =>
                prev.map(msg =>
                    msg.id === messageId ? { ...msg, isStreaming: false } : msg,
                ),
            );
            setIsLoadingSync(false);
            setPendingAutoConfirmId(toolCallId);
        } else {
            setMessages(prev =>
                prev.map(msg =>
                    msg.id === messageId ? { ...msg, confirmation, isStreaming: false } : msg,
                ),
            );
            setIsLoadingSync(false);
        }
    };

    /**
     * Nudge: invisible retry when a stream ended without the LLM producing a
     * text response.  Calls POST /agent/nudge (user_message=None) so no user
     * bubble is added to the conversation history.  Streams the response into
     * the existing (empty) assistant message.  Only retries once.
     */
    const performNudge = async (messageId: string) => {
        const cid = conversationIdRef.current;
        if (!cid) {
            // No conversation — show static fallback
            setMessages(prev =>
                prev.map(msg =>
                    msg.id === messageId
                        ? { ...msg, content: 'The response was interrupted. Please try again.', isStreaming: false }
                        : msg,
                ),
            );
            stopThinking();
            setIsLoadingSync(false);
            abortControllerRef.current = null;
            return;
        }

        // Brief delay so the backend's finally block can save conversation state
        await new Promise(r => setTimeout(r, 2000));

        const abortController = new AbortController();
        abortControllerRef.current = abortController;

        let currentMsgId = messageId;
        let hasContent = false;

        try {
            for await (const event of streamNudge(cid, abortController.signal)) {
                switch (event.event) {
                    case 'text': {
                        // Whitespace-only chunks: append to content but
                        // preserve tool indicators (see main chat text
                        // handler for rationale).
                        const isWhitespaceOnly = !event.data.replace(/\s+/g, '');
                        markActivity();
                        const targetId = currentMsgId;
                        setMessages(prev =>
                            prev.map(msg =>
                                msg.id === targetId
                                    ? {
                                          ...msg,
                                          content: msg.content + event.data,
                                          toolCalls: isWhitespaceOnly ? msg.toolCalls : undefined,
                                      }
                                    : msg,
                            ),
                        );
                        if (!isWhitespaceOnly) {
                            hasContent = true;
                        }
                        break;
                    }

                    case 'tool_call': {
                        markToolActivity();
                        const toolInfo = JSON.parse(event.data);
                        const targetId = currentMsgId;
                        setMessages(prev =>
                            prev.map(msg =>
                                msg.id === targetId
                                    ? { ...msg, toolCalls: [
                                        ...(msg.toolCalls?.filter(tc => tc.status === 'error') ?? []),
                                        toolInfo,
                                    ] }
                                    : msg,
                            ),
                        );
                        break;
                    }

                    case 'tool_error': {
                        const errorInfo = JSON.parse(event.data);
                        const targetId = currentMsgId;
                        setMessages(prev =>
                            prev.map(msg =>
                                msg.id === targetId && msg.toolCalls
                                    ? { ...msg, toolCalls: msg.toolCalls.map(tc =>
                                        tc.tool_call_id === errorInfo.tool_call_id
                                            ? { ...tc, status: 'error' as const }
                                            : tc,
                                    ) }
                                    : msg,
                            ),
                        );
                        break;
                    }

                    case 'chart': {
                        markActivity();
                        const chartSpec: ChartSpec = JSON.parse(event.data);
                        // Same dedicated-chart-bubble policy as the main
                        // chat path: finalise the chart bubble and create a
                        // fresh placeholder for subsequent status.
                        //
                        // Snapshot `currentMsgId` — see the main chat path
                        // for the full rationale (async updater would
                        // otherwise read the post-reassignment id).
                        const targetId = currentMsgId;
                        const newId = generateMessageId();
                        setMessages(prev => [
                            ...prev.map(msg =>
                                msg.id === targetId
                                    ? {
                                          ...msg,
                                          charts: [...(msg.charts ?? []), chartSpec],
                                          toolCalls: msg.toolCalls?.filter(tc => tc.status === 'error'),
                                          isStreaming: false,
                                      }
                                    : msg,
                            ),
                            { id: newId, role: 'assistant' as const, content: '', isStreaming: true },
                        ]);
                        currentMsgId = newId;
                        hasContent = false;
                        break;
                    }

                    case 'data_changed': {
                        const change: AgentDataChange = JSON.parse(event.data);
                        dispatchDataChanged(change);
                        break;
                    }

                    case 'confirmation_required': {
                        const confirmation: ConfirmationPayload = JSON.parse(event.data);
                        handleConfirmationRequired(confirmation.tool_call_id, confirmation, currentMsgId);
                        return;
                    }

                    case 'done': {
                        const targetId = currentMsgId;
                        setMessages(prev =>
                            prev
                                .map(msg => {
                                    if (msg.id !== targetId) return msg;
                                    return {
                                        ...msg,
                                        isStreaming: false,
                                        content: (msg.content || msg.charts?.length)
                                            ? msg.content
                                            : 'Sorry, I was unable to generate a response. Please try again.',
                                    };
                                })
                                .filter(msg => msg.role === 'user' || msg.content?.trim() || msg.charts?.length || msg.confirmation),
                        );
                        break;
                    }

                    case 'unavailable': {
                        setIsAvailable(false);
                        const targetId = currentMsgId;
                        setMessages(prev =>
                            prev.map(msg =>
                                msg.id === targetId
                                    ? { ...msg, content: event.data, isStreaming: false }
                                    : msg,
                            ),
                        );
                        break;
                    }

                    case 'error': {
                        const targetId = currentMsgId;
                        setMessages(prev =>
                            prev.map(msg =>
                                msg.id === targetId
                                    ? { ...msg, content: msg.content + `\n\nError: ${event.data}`, isStreaming: false }
                                    : msg,
                            ),
                        );
                        break;
                    }
                }
            }
        } catch {
            // Nudge also failed — show static fallback
            const targetId = currentMsgId;
            setMessages(prev =>
                prev.map(msg =>
                    msg.id === targetId
                        ? { ...msg, content: msg.content || 'Sorry, an error occurred. Please try again.', isStreaming: false }
                        : msg,
                ),
            );
        } finally {
            const targetId = currentMsgId;
            setMessages(prev =>
                prev
                    .map(msg => {
                        if (msg.id !== targetId) return msg;
                        if (!msg.isStreaming) return msg;
                        return { ...msg, isStreaming: false, content: msg.content || 'Sorry, an error occurred. Please try again.' };
                    })
                    .filter(msg => msg.role === 'user' || msg.content?.trim() || msg.charts?.length || msg.confirmation),
            );
            stopThinking();
            setIsLoadingSync(false);
            abortControllerRef.current = null;
        }
    };

    /**
     * Helper to run the main SSE event loop.  Shared by sendMessage and
     * handleConfirmation.  Returns { needsNudge, lastMessageId } so the
     * caller can decide whether to trigger the invisible retry.
     */
    const consumeEventStream = async (
        eventSource: AsyncGenerator<SSEEvent>,
        initialMessageId: string,
        sessionId: number,
        abortController: AbortController,
    ): Promise<{ needsNudge: boolean; lastMessageId: string }> => {
        let currentMessageId = initialMessageId;
        let currentHasToolCalls = false;
        let currentHasContent = false;
        let currentHasCharts = false;
        let receivedDone = false;

        // Stale-stream watchdog: if no SSE event reaches the handler for 45s,
        // assume the stream is stuck and abort so the user gets feedback.
        const STALE_STREAM_TIMEOUT_MS = 45_000;
        let isStaleAbort = false;
        let staleTimer: ReturnType<typeof setTimeout> | null = null;
        const resetStaleTimer = () => {
            if (staleTimer) clearTimeout(staleTimer);
            staleTimer = setTimeout(() => {
                isStaleAbort = true;
                abortController.abort();
            }, STALE_STREAM_TIMEOUT_MS);
        };
        resetStaleTimer();

        try {
            for await (const event of eventSource) {
                // If chat was cleared while streaming, abandon processing
                if (sessionIdRef.current !== sessionId) return { needsNudge: false, lastMessageId: currentMessageId };

                resetStaleTimer();

                switch (event.event) {
                    case 'conversation_id':
                        if (!conversationIdRef.current) {
                            setConversationIdSync(event.data);
                        }
                        break;

                    case 'text': {
                        setIsAvailable(true);
                        // Whitespace-only chunks (e.g. the `\n\n` between
                        // </think> and tool calls in Qwen3 output) get
                        // appended to content but must NOT clear the tool
                        // indicator or flip `currentHasContent`. Otherwise
                        // they prematurely finalise the bubble and cause a
                        // new bubble to spawn for the next tool_call.
                        const isWhitespaceOnly = !event.data.replace(/\s+/g, '');
                        markActivity();
                        if (currentHasCharts && !isWhitespaceOnly) {
                            // Chart message → split into new message so text appears below chart
                            // Snapshot id — updater is async, see chart handler.
                            const targetId = currentMessageId;
                            const newId = generateMessageId();
                            setMessages(prev => [
                                ...prev.map(msg =>
                                    msg.id === targetId ? { ...msg, isStreaming: false, toolCalls: undefined } : msg,
                                ),
                                { id: newId, role: 'assistant' as const, content: event.data, isStreaming: true },
                            ]);
                            currentMessageId = newId;
                            currentHasToolCalls = false;
                            currentHasCharts = false;
                        } else if (isWhitespaceOnly) {
                            // Append whitespace but preserve tool indicator state.
                            // Snapshot id — updater is async, currentMessageId
                            // may be mutated by a later event before React
                            // runs this updater.
                            const targetId = currentMessageId;
                            setMessages(prev =>
                                prev.map(msg =>
                                    msg.id === targetId
                                        ? { ...msg, content: msg.content + event.data }
                                        : msg,
                                ),
                            );
                        } else {
                            // Real content: append and clear tool indicators.
                            const targetId = currentMessageId;
                            setMessages(prev =>
                                prev.map(msg =>
                                    msg.id === targetId
                                        ? { ...msg, content: msg.content + event.data, toolCalls: undefined }
                                        : msg,
                                ),
                            );
                            currentHasToolCalls = false;
                        }
                        if (!isWhitespaceOnly) {
                            currentHasContent = true;
                        }
                        break;
                    }

                    case 'tool_call': {
                        markToolActivity();
                        const toolInfo = JSON.parse(event.data);
                        if (currentHasContent) {
                            // Text message has content → finalize it, start new message for tools
                            // Snapshot id — updater is async, see chart handler.
                            const targetId = currentMessageId;
                            const newId = generateMessageId();
                            setMessages(prev => [
                                ...prev.map(msg =>
                                    msg.id === targetId ? { ...msg, isStreaming: false } : msg,
                                ),
                                { id: newId, role: 'assistant' as const, content: '', isStreaming: true, toolCalls: [toolInfo] },
                            ]);
                            currentMessageId = newId;
                            currentHasContent = false;
                        } else {
                            // Add to current message's tool calls (replaces previous non-error ones for clean indicator)
                            const targetId = currentMessageId;
                            setMessages(prev =>
                                prev.map(msg =>
                                    msg.id === targetId
                                        ? { ...msg, toolCalls: [
                                            ...(msg.toolCalls?.filter(tc => tc.status === 'error') ?? []),
                                            toolInfo,
                                        ] }
                                        : msg,
                                ),
                            );
                        }
                        currentHasToolCalls = true;
                        break;
                    }

                    case 'chart': {
                        markActivity();
                        const chartSpec: ChartSpec = JSON.parse(event.data);
                        // Chart bubbles are dedicated: the chart is added to
                        // the current bubble and the bubble is finalised. A
                        // fresh placeholder bubble is created so subsequent
                        // tool indicators, working spinner, or follow-up
                        // chart events live in their own bubble — never
                        // mixed with chart content.
                        //
                        // We must snapshot `currentMessageId` BEFORE calling
                        // setMessages: the updater runs asynchronously (after
                        // the next event may have already mutated
                        // `currentMessageId` via `currentMessageId = newId`
                        // below). If we let the closure read the live value,
                        // it will look for a bubble that doesn't exist in
                        // `prev` yet, the chart is silently dropped, and the
                        // previous bubble never gets isStreaming=false — its
                        // "Fetching chart data..." indicator stays forever.
                        const targetId = currentMessageId;
                        const newId = generateMessageId();
                        setMessages(prev => [
                            ...prev.map(msg =>
                                msg.id === targetId
                                    ? {
                                          ...msg,
                                          charts: [...(msg.charts ?? []), chartSpec],
                                          toolCalls: msg.toolCalls?.filter(tc => tc.status === 'error'),
                                          isStreaming: false,
                                      }
                                    : msg,
                            ),
                            { id: newId, role: 'assistant' as const, content: '', isStreaming: true },
                        ]);
                        currentMessageId = newId;
                        currentHasCharts = false;
                        currentHasToolCalls = false;
                        currentHasContent = false;
                        break;
                    }

                    case 'tool_error': {
                        const errorInfo = JSON.parse(event.data);
                        const targetId = currentMessageId;
                        setMessages(prev =>
                            prev.map(msg =>
                                msg.id === targetId && msg.toolCalls
                                    ? {
                                          ...msg,
                                          toolCalls: msg.toolCalls.map(tc =>
                                              tc.tool_call_id === errorInfo.tool_call_id
                                                  ? { ...tc, status: 'error' as const }
                                                  : tc,
                                          ),
                                      }
                                    : msg,
                            ),
                        );
                        break;
                    }

                    case 'confirmation_required': {
                        const confirmation: ConfirmationPayload = JSON.parse(event.data);
                        handleConfirmationRequired(confirmation.tool_call_id, confirmation, currentMessageId);
                        // Graceful exit — no nudge needed
                        return { needsNudge: false, lastMessageId: currentMessageId };
                    }

                    case 'data_changed': {
                        const change: AgentDataChange = JSON.parse(event.data);
                        dispatchDataChanged(change);
                        break;
                    }

                    case 'done': {
                        receivedDone = true;
                        const targetId = currentMessageId;
                        setMessages(prev =>
                            prev
                                .map(msg => {
                                    if (msg.id !== targetId) return msg;
                                    const hasContent = msg.content || msg.charts?.length;
                                    return {
                                        ...msg,
                                        isStreaming: false,
                                        // If the agent finished without producing text, show a fallback
                                        content: hasContent
                                            ? msg.content
                                            : 'I processed your request but couldn\'t generate a complete response. Please try again or rephrase your question.',
                                    };
                                })
                                // Remove empty assistant bubbles left over from tool-call splits
                                .filter(msg => msg.role === 'user' || msg.content?.trim() || msg.charts?.length || msg.confirmation),
                        );
                        break;
                    }

                    case 'unavailable': {
                        setIsAvailable(false);
                        const targetId = currentMessageId;
                        setMessages(prev =>
                            prev.map(msg =>
                                msg.id === targetId
                                    ? { ...msg, content: event.data, isStreaming: false }
                                    : msg,
                            ),
                        );
                        break;
                    }

                    case 'error': {
                        setIsAvailable(false);
                        const targetId = currentMessageId;
                        setMessages(prev =>
                            prev.map(msg =>
                                msg.id === targetId
                                    ? { ...msg, content: msg.content + `\n\nError: ${event.data}`, isStreaming: false }
                                    : msg,
                            ),
                        );
                        break;
                    }
                }
            }

            // Stream ended — if no done event and no content, we need a nudge
            if (!receivedDone && !currentHasContent) {
                return { needsNudge: true, lastMessageId: currentMessageId };
            }
            return { needsNudge: false, lastMessageId: currentMessageId };

        } catch (err: any) {
            if (err.name === 'AbortError' && isStaleAbort) {
                // Stale stream detected — nudge to recover
                return { needsNudge: true, lastMessageId: currentMessageId };
            }
            if (err.name !== 'AbortError') {
                // Real error — show message, no nudge
                setIsAvailable(false);
                const targetId = currentMessageId;
                setMessages(prev =>
                    prev.map(msg =>
                        msg.id === targetId
                            ? { ...msg, content: msg.content || 'Sorry, an error occurred. Please try again.', isStreaming: false }
                            : msg,
                    ),
                );
            }
            return { needsNudge: false, lastMessageId: currentMessageId };
        } finally {
            if (staleTimer) clearTimeout(staleTimer);
        }
    };

    const sendMessage = useCallback(async (content: string) => {
        if (!content.trim() || isLoadingRef.current) return;

        const userMessage: ChatMessage = {
            id: generateMessageId(),
            role: 'user',
            content: content.trim(),
        };
        const assistantMessageId = generateMessageId();
        const assistantMessage: ChatMessage = {
            id: assistantMessageId,
            role: 'assistant',
            content: '',
            isStreaming: true,
        };

        setMessages(prev => [...prev, userMessage, assistantMessage]);
        setIsLoadingSync(true);
        startThinking();

        const abortController = new AbortController();
        abortControllerRef.current = abortController;
        const sessionId = sessionIdRef.current;

        const eventSource = streamChat(
            content.trim(),
            conversationIdRef.current,
            contextRef.current,
            abortController.signal,
            autoConfirmRef.current,
        );

        const { needsNudge, lastMessageId } = await consumeEventStream(
            eventSource, assistantMessageId, sessionId, abortController,
        );

        if (needsNudge) {
            await performNudge(lastMessageId);
        } else {
            // Normal cleanup: finalize any still-streaming message
            setMessages(prev =>
                prev
                    .map(msg => {
                        if (msg.id !== lastMessageId) return msg;
                        if (!msg.isStreaming) return msg;
                        const isEmpty = !msg.content && !msg.charts?.length;
                        return {
                            ...msg,
                            isStreaming: false,
                            content: isEmpty
                                ? 'The response was interrupted. Please try sending your message again.'
                                : msg.content,
                        };
                    })
                    .filter(msg => msg.role === 'user' || msg.content?.trim() || msg.charts?.length || msg.confirmation),
            );
            stopThinking();
            setIsLoadingSync(false);
            abortControllerRef.current = null;
        }
    }, []); // stable — reads live values via refs

    const handleConfirmation = useCallback(async (toolCallId: string, confirmed: boolean) => {
        const currentConversationId = conversationIdRef.current;
        if (!currentConversationId) return;

        setMessages(prev =>
            prev.map(msg =>
                msg.confirmation?.tool_call_id === toolCallId
                    ? { ...msg, confirmation: undefined, toolCalls: msg.toolCalls?.filter(tc => tc.status === 'error') }
                    : msg,
            ),
        );

        const followUpId = generateMessageId();
        setMessages(prev => [
            ...prev,
            { id: followUpId, role: 'assistant', content: '', isStreaming: true },
        ]);
        setIsLoadingSync(true);
        startThinking();

        const abortController = new AbortController();
        abortControllerRef.current = abortController;
        const sessionId = sessionIdRef.current;

        const eventSource = streamConfirmation(
            currentConversationId,
            toolCallId,
            confirmed,
            abortController.signal,
            autoConfirmRef.current,
        );

        const { needsNudge, lastMessageId } = await consumeEventStream(
            eventSource, followUpId, sessionId, abortController,
        );

        if (needsNudge) {
            await performNudge(lastMessageId);
        } else {
            // Normal cleanup
            setMessages(prev =>
                prev
                    .map(msg => {
                        if (msg.id !== lastMessageId) return msg;
                        if (!msg.isStreaming) return msg;
                        const isEmpty = !msg.content && !msg.charts?.length;
                        return {
                            ...msg,
                            isStreaming: false,
                            content: isEmpty
                                ? 'The response was interrupted. Please try sending your message again.'
                                : msg.content,
                        };
                    })
                    .filter(msg => msg.role === 'user' || msg.content?.trim() || msg.charts?.length || msg.confirmation),
            );
            stopThinking();
            setIsLoadingSync(false);
            abortControllerRef.current = null;
        }
    }, []); // stable — reads live values via refs

    const stopGeneration = useCallback(() => {
        abortControllerRef.current?.abort();
        stopThinking();
        setIsLoadingSync(false);
    }, []);

    const clearChat = useCallback(() => {
        // Abort any in-flight SSE stream to prevent cross-session state leakage
        abortControllerRef.current?.abort();
        abortControllerRef.current = null;
        // Increment session so any lagging events from the old stream are dropped
        sessionIdRef.current += 1;

        setMessages([]);
        conversationIdRef.current = null;
        setConversationId(null);
        autoConfirmRef.current = false;
        setAutoConfirmState(false);
        setIsAvailable(null);
        stopThinking();
        setIsLoadingSync(false);
        setPendingAutoConfirmId(null);
    }, []);

    const loadConversation = useCallback((id: string, displayMessages: DisplayMessage[]) => {
        // Abort any in-flight SSE stream to prevent cross-session state leakage
        abortControllerRef.current?.abort();
        abortControllerRef.current = null;
        // Increment session so any lagging events from the old stream are dropped
        sessionIdRef.current += 1;

        // Convert display messages to ChatMessage format
        const chatMessages: ChatMessage[] = displayMessages.map(msg => ({
            id: generateMessageId(),
            role: msg.role,
            content: msg.content,
            toolCalls: msg.tool_calls ?? undefined,
            charts: msg.charts as ChartSpec[] | undefined,
        }));
        setMessages(chatMessages);
        setConversationIdSync(id);
        autoConfirmRef.current = false;
        setAutoConfirmState(false);
        stopThinking();
        setIsLoadingSync(false);
        setPendingAutoConfirmId(null);
    }, []);

    return {
        messages,
        isLoading,
        isThinking,
        isAvailable,
        conversationId,
        autoConfirm,
        setAutoConfirm,
        sendMessage,
        handleConfirmation,
        stopGeneration,
        clearChat,
        loadConversation,
    };
}
